import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  CalendarOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons";
import ElsnerLogo from "../../assets/images/Logowhite.svg";
import Service from "../../service";
import { footerText } from "util/config";
import "../../assets/css/public-holidays.css";

// This page is open to everyone — anonymous visitors and signed-in employees alike —
// so it must never touch the auth stack. Service.makeAPICall is off-limits here: it
// registers the global interceptors and runs permissionChange() on the response, and
// the public endpoint returns an empty `permissions` array, which that helper reads as
// a revoked grant and reacts to by clearing localStorage and reloading (i.e. it would
// silently sign out any employee who opened this page). A bare axios instance has no
// interceptors and sends no Authorization header.
const publicClient = axios.create({ baseURL: Service.API_URL });

// Holiday dates are stored as UTC midnight, so every date in this page is built and
// compared in UTC. Parsing them in the browser's local zone would shift a 1 Jan
// holiday into 31 Dec for anyone west of Greenwich — wrong cell, wrong month.
const asUtcDay = (date) => moment.utc(date).startOf("day");
const utcDate = (year, month, day) => moment.utc([year, month, day]);

// Several holiday names carry stray trailing tabs from the admin form, so trim
// before splitting — otherwise they render as a ragged gap after the name.
const titleCase = (str) =>
  String(str || "")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const PublicHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(moment().year());
  const [activeMonth, setActiveMonth] = useState(moment.utc().month());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchHolidays = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await publicClient.get(Service.publicHolidays);
      if (data && Array.isArray(data.data)) {
        setHolidays(data.data);
        if (data.year) setYear(data.year);
      } else {
        setHolidays([]);
        setError(data?.message || "Unable to load the holiday list right now.");
      }
    } catch (e) {
      setHolidays([]);
      setError("Unable to load the holiday list right now. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = `Holiday List ${moment().year()} | Elsner Technologies`;
    fetchHolidays();
  }, [fetchHolidays]);

  // One pass over the list feeds both views: the calendar looks holidays up by
  // "YYYY-MM-DD", the side panel walks them in order.
  const { list, byDate, stats, nextHoliday } = useMemo(() => {
    const today = moment.utc().startOf("day");

    const decorated = holidays
      .map((holiday) => {
        const date = asUtcDay(holiday.holiday_date);
        const diff = date.diff(today, "days");
        return {
          ...holiday,
          date,
          key: date.format("YYYY-MM-DD"),
          name: titleCase(holiday.holiday_name),
          isPast: diff < 0,
          isToday: diff === 0,
        };
      })
      .sort((a, b) => a.date.valueOf() - b.date.valueOf());

    const lookup = {};
    decorated.forEach((holiday) => {
      lookup[holiday.key] = holiday;
    });

    return {
      list: decorated,
      byDate: lookup,
      stats: {
        total: decorated.length,
        upcoming: decorated.filter((h) => !h.isPast).length,
        past: decorated.filter((h) => h.isPast).length,
      },
      nextHoliday: decorated.find((h) => !h.isPast) || null,
    };
  }, [holidays]);

  // Land on the month the next holiday falls in — opening on an empty current
  // month would hide the one thing most visitors came to check.
  useEffect(() => {
    if (nextHoliday) setActiveMonth(nextHoliday.date.month());
  }, [nextHoliday]);

  // Leading blanks pad the first row so the 1st lands under its real weekday, and
  // trailing blanks fill out a fixed 6-week grid — a month that needs only four or
  // five rows would otherwise shrink the card, and the list beside it with it.
  const cells = useMemo(() => {
    const firstOfMonth = utcDate(year, activeMonth, 1);
    const out = [];
    for (let i = 0; i < firstOfMonth.day(); i += 1) out.push(null);
    for (let day = 1; day <= firstOfMonth.daysInMonth(); day += 1) {
      out.push(utcDate(year, activeMonth, day));
    }
    while (out.length < 42) out.push(null);
    return out;
  }, [year, activeMonth]);

  const today = moment.utc().startOf("day");
  const monthHolidayCount = list.filter((h) => h.date.month() === activeMonth).length;

  const renderCalendar = () => (
    <section className="ph-cal">
      <header className="ph-cal-head">
        <button
          type="button"
          className="ph-cal-nav"
          onClick={ () => setActiveMonth((m) => Math.max(0, m - 1)) }
          disabled={ activeMonth === 0 }
          aria-label="Previous month"
        >
          <LeftOutlined />
        </button>

        <div className="ph-cal-title">
          <h2>
            { moment.months(activeMonth) } <span>{ year }</span>
          </h2>
          <p>
            { monthHolidayCount
              ? `${monthHolidayCount} ${monthHolidayCount === 1 ? "holiday" : "holidays"} this month`
              : "No holidays this month" }
          </p>
        </div>

        <button
          type="button"
          className="ph-cal-nav"
          onClick={ () => setActiveMonth((m) => Math.min(11, m + 1)) }
          disabled={ activeMonth === 11 }
          aria-label="Next month"
        >
          <RightOutlined />
        </button>
      </header>

      <div className="ph-cal-weekdays">
        { WEEKDAYS.map((day) => (
          <span key={ day }>{ day }</span>
        )) }
      </div>

      <div className="ph-cal-grid">
        { cells.map((date, index) => {
          if (!date) return <span className="ph-cell is-blank" key={ `blank-${index}` } />;

          const holiday = byDate[date.format("YYYY-MM-DD")];
          const isToday = date.isSame(today, "day");
          const isWeekend = date.day() === 0 || date.day() === 6;
          const isPast = date.isBefore(today, "day");

          const classes = [
            "ph-cell",
            holiday ? "is-holiday" : "",
            holiday && isPast ? "is-past" : "",
            isToday ? "is-today" : "",
            isWeekend && !holiday ? "is-weekend" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <span
              className={ classes }
              key={ date.format("YYYY-MM-DD") }
              title={ holiday ? `${holiday.name} — ${date.format("dddd, DD MMMM YYYY")}` : undefined }
            >
              <span className="ph-cell-num">{ date.format("D") }</span>
              { holiday && (
                <>
                  <span className="ph-cell-name">{ holiday.name }</span>
                  <span className="ph-cell-dot" />
                </>
              ) }
            </span>
          );
        }) }
      </div>

      <footer className="ph-cal-legend">
        <span><i className="ph-key ph-key-holiday" /> Holiday</span>
        <span><i className="ph-key ph-key-today" /> Today</span>
        <span><i className="ph-key ph-key-past" /> Passed</span>
      </footer>
    </section>
  );

  const renderList = () => (
    <aside className="ph-side">
      <header className="ph-side-head">
        <h2>All holidays</h2>
        <span>{ year }</span>
      </header>

      { /* The scroller is absolutely positioned inside this wrapper so a long list
           adds nothing to the card's intrinsic height — that keeps the card the same
           height as the calendar beside it and scrolls the overflow instead. */ }
      <div className="ph-side-scroll">
        { list.length ? (
          <ul className="ph-side-list">
            { list.map((holiday) => (
              <li key={ holiday._id || holiday.key }>
                <button
                  type="button"
                  className={ `ph-side-item${holiday.isPast ? " is-past" : ""}${holiday.isToday ? " is-today" : ""
                    }${holiday.date.month() === activeMonth ? " is-active" : ""}` }
                  onClick={ () => setActiveMonth(holiday.date.month()) }
                >
                  <span className="ph-side-date">
                    <strong>{ holiday.date.format("DD") }</strong>
                    <em>{ holiday.date.format("MMM") }</em>
                  </span>
                  <span className="ph-side-text">
                    <span className="ph-side-name">{ holiday.name }</span>
                    <span className="ph-side-day">
                      { holiday.date.format("dddd, DD MMMM YYYY") }
                    </span>
                  </span>
                  { holiday.isToday && <span className="ph-chip">Today</span> }
                </button>
              </li>
            )) }
          </ul>
        ) : (
          <p className="ph-side-none">No holidays published for { year } yet.</p>
        ) }
      </div>
    </aside>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="ph-layout">
          <div className="ph-skeleton-block ph-skeleton-cal" />
          <div className="ph-skeleton-block ph-skeleton-side" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="ph-empty">
          <CalendarOutlined className="ph-empty-icon" />
          <p>{ error }</p>
          <button type="button" className="ph-retry" onClick={ fetchHolidays }>
            <ReloadOutlined /> Try again
          </button>
        </div>
      );
    }

    return (
      <div className="ph-layout">
        { renderCalendar() }
        { renderList() }
      </div>
    );
  };

  return (
    <div className="ph-page">
      {/* Logo, title and the year's headline numbers all live on one full-bleed
          brand banner; .ph-main is then pulled up so the first card overlaps it. */}
      <header className="ph-hero">
        <div className="ph-hero-inner">
          <a className="ph-logo" href="/">
            <img src={ ElsnerLogo } alt="Elsner Technologies" />
          </a>

          <p className="ph-eyebrow">Elsner Technologies Pvt. Ltd.</p>
          <h1 className="ph-title">
            Holiday List <span>{ year }</span>
          </h1>

          { !loading && !error && (
            <div className="ph-stats">
              <div className="ph-stat">
                <span className="ph-stat-value">{ stats.total }</span>
                <span className="ph-stat-label">Holidays</span>
              </div>
              <div className="ph-stat">
                <span className="ph-stat-value">{ stats.upcoming }</span>
                <span className="ph-stat-label">Upcoming</span>
              </div>
              <div className="ph-stat">
                <span className="ph-stat-value">{ stats.past }</span>
                <span className="ph-stat-label">Passed</span>
              </div>
            </div>
          ) }
        </div>
      </header>

      <main className="ph-main">
        { !loading && !error && (
          <div className="ph-next">
            <span className="ph-next-mark" />
            <div className="ph-next-body">
              <span className="ph-next-label">
                { nextHoliday && nextHoliday.isToday ? "Today is" : "Next holiday" }
              </span>
              <strong className="ph-next-name">
                { nextHoliday ? nextHoliday.name : `That's a wrap for ${year}` }
              </strong>
            </div>
            <span className="ph-next-date">
              <CalendarOutlined />
              { nextHoliday
                ? nextHoliday.date.format("dddd, DD MMMM YYYY")
                : "No holidays left this year" }
            </span>
            { nextHoliday && !nextHoliday.isToday && (
              <span className="ph-next-count">
                { nextHoliday.date.diff(today, "days") } days to go
              </span>
            ) }
          </div>
        ) }

        { renderBody() }
      </main>

      <footer className="ph-footer">{ footerText }</footer>
    </div>
  );
};

export default PublicHolidays;
